import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Mail, ShieldCheck, ScanLine, Loader2, CheckCircle2, Copy } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { sb } from "@/lib/sb";
import { runSecurityAudit, listAuditRuns } from "@/lib/security-audit.functions";

export const Route = createFileRoute("/_authenticated/dashboard/admin/security")({
  component: SecurityPage,
});

type AuditRun = { id: string; started_at: string; finished_at: string | null; status: string; total_findings: number; high: number; medium: number; low: number; trigger: string };
type AuditFinding = { id: string; severity: string; category: string; title: string; detail: string | null };

function AuditPanel() {
  const run = useServerFn(runSecurityAudit);
  const list = useServerFn(listAuditRuns);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState<AuditRun[]>([]);
  const [findings, setFindings] = useState<AuditFinding[]>([]);

  const refresh = async () => {
    try {
      const r = await list({});
      setRuns(r.runs as AuditRun[]);
      setFindings(r.latestFindings as AuditFinding[]);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { refresh(); }, []);

  const triggerRun = async () => {
    setRunning(true);
    try {
      await run({});
      toast.success("Audit complete");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Audit failed");
    } finally { setRunning(false); }
  };

  const sevColor = (s: string) => s === "high"
    ? "bg-destructive/15 text-destructive border-destructive/40"
    : s === "medium" ? "bg-yellow-500/15 text-yellow-700 border-yellow-500/40"
    : "bg-muted text-muted-foreground border-border";

  const buildFixPrompt = (f: AuditFinding): string | null => {
    const t = `${f.title} ${f.detail ?? ""}`.toLowerCase();
    if (t.includes("content-security-policy") || t.includes("csp")) {
      return "Add a Content-Security-Policy response header to the public site via the hosting/headers config (not a <meta> tag — that's only a partial fallback). Start from this baseline and adjust to the site's real third parties (Google Analytics, Supabase, Google Fonts):\n\n  Content-Security-Policy: default-src 'self'; img-src 'self' data: https:; script-src 'self' 'unsafe-inline' https://www.googletagmanager.com https://www.google-analytics.com; style-src 'self' 'unsafe-inline'; connect-src 'self' https://*.supabase.co https://www.google-analytics.com; frame-ancestors 'none'\n\nDeploy to a preview first and verify the site still loads with no console CSP violations before publishing — a too-strict CSP blocks scripts/styles and breaks the site.";
    }
    if (t.includes("strict-transport-security") || t.includes("hsts")) {
      return "Add a Strict-Transport-Security response header in the hosting/headers config:\n\n  Strict-Transport-Security: max-age=63072000; includeSubDomains; preload\n\nOnly enable `preload` once HTTPS is enforced sitewide on the apex and all subdomains.";
    }
    if (t.includes("x-content-type-options") || t.includes("nosniff")) {
      return "Add an X-Content-Type-Options response header in the hosting/headers config:\n\n  X-Content-Type-Options: nosniff";
    }
    if (t.includes("referrer-policy")) {
      return "Add a Referrer-Policy response header in the hosting/headers config:\n\n  Referrer-Policy: strict-origin-when-cross-origin";
    }
    if (t.includes("x-frame-options") || t.includes("clickjack") || t.includes("frame-ancestors")) {
      return "Add an X-Frame-Options response header in the hosting/headers config (or rely on CSP `frame-ancestors 'none'` if a CSP is in place):\n\n  X-Frame-Options: DENY";
    }
    if (t.includes("https") && (t.includes("not enforced") || t.includes("redirect") || t.includes("http "))) {
      return "Enforce HTTPS at the host: add a permanent redirect from http:// to https:// for all paths, and verify the canonical domain (apex + www) both redirect to the chosen https origin.";
    }
    if (t.includes("robots") || t.includes("sitemap")) {
      return "Fix robots.txt: ensure it is reachable at /robots.txt, allows crawling of public pages, and includes a `Sitemap: https://www.homesteadconstruction.us/sitemap.xml` line. Verify /sitemap.xml is reachable and lists all public URLs.";
    }
    if (t.includes("failed sign-in") || t.includes("failed login") || t.includes("sign-in spike")) {
      return null; // not auto-fixable
    }
    // Generic header / config remediation fallback
    if (f.category?.toLowerCase().includes("header") || t.includes("header")) {
      return `Address the security finding "${f.title}" by updating the site's hosting/headers config. Detail: ${f.detail ?? "(no extra detail)"}. Verify the site still loads on a preview before publishing.`;
    }
    return null;
  };

  const fixableFindings = findings
    .map((f) => ({ finding: f, prompt: buildFixPrompt(f) }))
    .filter((x): x is { finding: AuditFinding; prompt: string } => x.prompt !== null);

  const copyFix = async (f: AuditFinding, prompt: string) => {
    await navigator.clipboard.writeText(
      `Please fix this security finding on Homestead Construction:\n\n[${f.severity.toUpperCase()}] ${f.title}\n${f.detail ?? ""}\n\n${prompt}`,
    );
    toast.success("Fix prompt copied. Paste into Lovable to apply.");
  };

  const copyAllFixes = async () => {
    if (!fixableFindings.length) return;
    const body = [
      "Please fix these security findings on Homestead Construction. Do NOT auto-apply without verifying the site still loads:",
      ...fixableFindings.map(
        ({ finding, prompt }, i) =>
          `\n${i + 1}. [${finding.severity.toUpperCase()}] ${finding.title}\n   ${finding.detail ?? ""}\n   ${prompt}`,
      ),
    ].join("\n");
    await navigator.clipboard.writeText(body);
    toast.success(`Copied ${fixableFindings.length} fix prompt${fixableFindings.length === 1 ? "" : "s"}.`);
  };

  const latest = runs[0];
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ScanLine size={18} className="text-brand" />
          <h2 className="font-display font-semibold text-ink">Automated security audit</h2>
        </div>
        <div className="flex items-center gap-2">
          {fixableFindings.length > 0 && (
            <button
              onClick={copyAllFixes}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand text-primary-foreground text-xs font-semibold"
            >
              <Copy size={12} /> Fix all in Lovable ({fixableFindings.length})
            </button>
          )}
          <button onClick={triggerRun} disabled={running}
            className="px-4 py-1.5 rounded-lg bg-card border border-border text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2 hover:border-brand">
            {running ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
            {running ? "Scanning…" : "Run audit now"}
          </button>
        </div>
      </div>
      {latest && (
        <div className="text-xs text-muted-foreground">
          Last run {new Date(latest.started_at).toLocaleString()} — {latest.total_findings} findings
          ({latest.high} high, {latest.medium} medium, {latest.low} low)
        </div>
      )}
      {findings.length === 0 && latest && (
        <div className="flex items-center gap-2 text-emerald-600 text-sm">
          <CheckCircle2 size={16} /> No issues found in the latest audit.
        </div>
      )}
      {findings.length > 0 && (
        <ul className="space-y-2">
          {findings.map((f) => {
            const prompt = buildFixPrompt(f);
            return (
              <li key={f.id} className="flex gap-3 p-3 border border-border rounded-lg bg-background">
                <span className={`shrink-0 px-2 py-0.5 rounded border text-xs uppercase ${sevColor(f.severity)}`}>{f.severity}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink">{f.title}</div>
                  {f.detail && <div className="text-xs text-muted-foreground mt-1">{f.detail}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">{f.category}</div>
                  {!prompt && (
                    <div className="text-xs text-muted-foreground mt-2 italic">
                      Not auto-fixable. Review the sign-in events below; consider rate-limiting or a temporary lockout after N failed attempts in the auth provider.
                    </div>
                  )}
                </div>
                {prompt ? (
                  <button
                    onClick={() => copyFix(f, prompt)}
                    className="shrink-0 self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-brand text-primary-foreground text-xs font-semibold"
                    title="Copy a tailored remediation prompt for this finding"
                  >
                    <Copy size={11} /> Fix in Lovable
                  </button>
                ) : (
                  <button
                    disabled
                    className="shrink-0 self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted text-muted-foreground text-xs font-semibold cursor-not-allowed"
                    title="This finding requires manual review — no code change can fix it."
                  >
                    Manual review
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


type Event = {
  id: string;
  ts: string;
  email_attempted: string | null;
  success: boolean;
  event_type: string;
};

type Settings = {
  security_report_email: string | null;
  speed_report_email: string | null;
};

function SecurityPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [status, setStatus] = useState<"all" | "success" | "fail">("all");
  const [settings, setSettings] = useState<Settings>({
    security_report_email: "",
    speed_report_email: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const since = new Date(Date.now() - 30 * 86400 * 1000).toISOString();
    Promise.all([
      sb.from("security_events")
        .select("*")
        .gte("ts", since)
        .order("ts", { ascending: false })
        .limit(500),
      sb.from("app_settings").select("security_report_email, speed_report_email").eq("id", 1).maybeSingle(),
    ]).then(([eventsRes, settingsRes]: any) => {
      const { data, error } = eventsRes;
      const { data: settingsData } = settingsRes;
        if (error) toast.error(error.message);
        setEvents(data ?? []);
        if (settingsData) setSettings(settingsData);
      });
  }, []);

  const saveRecipients = async () => {
    setSaving(true);
    const { error } = await sb.from("app_settings").update(settings).eq("id", 1);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Recipients saved");
  };

  const failed30 = events.filter((e) => !e.success).length;
  const failed7 = events.filter(
    (e) => !e.success && new Date(e.ts).getTime() > Date.now() - 7 * 86400 * 1000,
  ).length;
  const filtered = events.filter((e) =>
    status === "all" ? true : status === "success" ? e.success : !e.success,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
          <ShieldCheck size={22} className="text-brand" /> Security
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Sign-in activity and report recipients in one place, matching the Blaine admin structure.
        </p>
      </div>

      <AuditPanel />

      {failed7 > 10 && (
        <div className="bg-destructive/10 border border-destructive/40 rounded-xl p-4 flex gap-3 text-destructive">
          <AlertTriangle size={20} />
          <p className="text-sm font-medium">
            {failed7} failed login attempts in the last 7 days. Review activity.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Failed logins (30d)</p>
          <p className="font-display text-2xl font-bold mt-1 text-ink">{failed30}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Total events (30d)</p>
          <p className="font-display text-2xl font-bold mt-1 text-ink">{events.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Security report email</p>
          <p className="font-display text-sm font-semibold mt-2 text-ink truncate">
            {settings.security_report_email || "Not set"}
          </p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Speed report email</p>
          <p className="font-display text-sm font-semibold mt-2 text-ink truncate">
            {settings.speed_report_email || "Not set"}
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex gap-2 mb-3">
          {(["all", "success", "fail"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-sm capitalize ${
                status === s
                  ? "bg-brand text-primary-foreground"
                  : "bg-background border border-border"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-xs">
              <tr>
                <th className="text-left py-2">When</th>
                <th className="text-left">Event</th>
                <th className="text-left">Email</th>
                <th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="py-1.5 text-xs text-muted-foreground">
                    {new Date(e.ts).toLocaleString()}
                  </td>
                  <td className="py-1.5">{e.event_type}</td>
                  <td className="py-1.5 truncate max-w-[200px]">{e.email_attempted}</td>
                  <td className="py-1.5 text-right">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        e.success
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-destructive/15 text-destructive"
                      }`}
                    >
                      {e.success ? "OK" : "Fail"}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground">
                    No events recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4 max-w-2xl">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-brand" />
          <h2 className="font-display font-semibold text-ink">Audit email recipients</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Each completed security or speed review should go to the saved recipients below.
        </p>
        {[
          ["security_report_email", "Security report email"],
          ["speed_report_email", "Speed report email"],
        ].map(([key, label]) => (
          <div key={key} className="space-y-2">
            <label className="text-xs text-muted-foreground">{label}</label>
            <input
              type="email"
              value={(settings as any)[key] ?? ""}
              onChange={(event) => setSettings({ ...settings, [key]: event.target.value })}
              placeholder="email@example.com"
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm"
            />
          </div>
        ))}
        <button
          onClick={saveRecipients}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-brand text-primary-foreground font-semibold text-sm disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save recipients"}
        </button>
      </div>
    </div>
  );
}
